import { z } from 'zod';
import { MAX_STORED_TEXT_LENGTH, richTextDocSchema } from './richtext.js';
import { fileAttachmentSchema } from './file.js';
import { linkPreviewSchema } from './link.js';
export const messageSubtypeSchema = z.enum([
    'standard',
    'member_joined',
    'member_left',
    'channel_created',
    'topic_changed',
    'channel_renamed',
    /** A csatorna nyilvanossa vagy privatta valt - ez mindenkit erint. */
    'visibility_changed',
    'huddle_started',
    /**
     * NEM FOGADOTT HIVAS.
     *
     * A hivas veget jelzi azoknak, akiket behivtak, de nem vettek fel. A
     * `meta.missedUserIds` mondja meg, KIT kerestek: ugyanaz a sor mindenki
     * elozmenyeben ott all, de csak nekik szol szemelyesen - ahogy egy emlites
     * is kozos uzenetben all, megis egyvalakinek szol.
     */
    'huddle_missed',
    /** Vendeg (fiok nelkuli kulso fel) csatlakozott a hivashoz - H2. */
    'huddle_guest',
    /**
     * Az olelesben tartott SZAVAZAS EREDMENYE.
     *
     * A `meta.result` a szerkezetes eredmeny (kerdes, megoszlas), a szoveg pedig
     * ugyanennek az egyszeru valtozata - a kereseshez, ertesiteshez.
     */
    'huddle_poll',
    /**
     * A "NAPTAR" uzenete (docs/23): meghivo, modositas, lemondas vagy a szervezonek
     * a valaszok osszesitese. A `meta` a `calendarMessageMetaSchema` szerinti.
     */
    'calendar',
]);
/**
 * CSENDES SOR: a telefonos hivasnaplo bejegyzese (bejovo vagy kimeno hivas).
 *
 * Ott all a folyamban, de NEM OLVASATLAN: egy telefonos csatornaban minden
 * hivas ir egy sort, es ha mindegyik jelzest adna, a csatorna sosem lenne
 * "kesz" - pont a nem fogadott hivas veszne el a tobbi kozott (Adam kerese,
 * 2026-10-22). A nem fogadott hivasnak sajat sora van (`huddle_missed`), az
 * tovabbra is jelez.
 */
export function isQuietMessage(message) {
    return message.subtype === 'huddle_started' && message.meta?.phone === true;
}
/**
 * A szallitasi felso hatar - NEM ez a tenyleges korlat.
 *
 * Az ertelmes korlat mufajra bontva ervenyes (`MAX_PROSE_LENGTH` es
 * `MAX_CODE_LENGTH`), es a szerveren, a `prepareBody`-ban dol el: kodblokk
 * nelkul a szoveg a proza-korlatot kapja. Ez a szam csak azt mondja meg,
 * mekkora mezot fogadunk el egyaltalan, hogy a Zod ne engedjen be korlatlan
 * meretu stringet a szemantikai ellenorzes ele.
 */
export const MAX_MESSAGE_LENGTH = MAX_STORED_TEXT_LENGTH;
/** Egy emoji osszesitve: hanyan es kik reagaltak vele. */
export const reactionSummarySchema = z.object({
    emoji: z.string(),
    count: z.number(),
    userIds: z.array(z.string()),
});
export const MAX_REACTIONS_PER_MESSAGE = 24;
export const messageTaskSchema = z.object({
    done: z.boolean(),
    doneBy: z.string().nullable(),
    doneAt: z.string().nullable(),
});
export const messageSchema = z.object({
    id: z.string(),
    channelId: z.string(),
    /** Csatornankent szigoruan monoton, LYUKMENTES sorszam. */
    seq: z.number(),
    userId: z.string().nullable(),
    parentId: z.string().nullable(),
    text: z.string(),
    subtype: messageSubtypeSchema,
    meta: z.record(z.string(), z.unknown()).nullable(),
    clientMsgId: z.string().nullable(),
    createdAt: z.string(),
    editedAt: z.string().nullable(),
    deletedAt: z.string().nullable(),
    /**
     * Formazott tartalom. Ha null, az uzenet sima szoveg (bot, webhook, rendszeruzenet).
     * A `text` mindig kitoltott: ebbol keszul a kereses es az ertesites.
     */
    body: richTextDocSchema.nullable(),
    reactions: z.array(reactionSummarySchema),
    files: z.array(fileAttachmentSchema),
    /** Link-elonezetek. Hatterben keszulnek, ezert kesobb is megjelenhetnek. */
    links: z.array(linkPreviewSchema),
    /** A csatornahoz kituzve - mindenki latja. */
    isPinned: z.boolean(),
    /**
     * FONTOS uzenet.
     *
     * A kuldo dontese, kuldes idejen. Nem ugyanaz, mint a kituzes: a kituzes a
     * CSATORNA dolga (barki teheti, barmikor, es a fejlecbol elerheto), ez pedig
     * magarol az uzenetrol szol - arrol, hogy a szerzoje szerint ezt nem szabad
     * atgorgetni.
     */
    isImportant: z.boolean(),
    /**
     * FELADAT: `null` / hianyzik = nem feladat. Elvegezve: ki es mikor pipalta ki.
     * A csatorna fejleceben a nyitott feladatok listaja latszik.
     */
    task: messageTaskSchema.nullable().optional(),
    /** A KERO felhasznalo mentette-e el. Felhasznalonkent elteroen jon vissza. */
    isSaved: z.boolean(),
    /** Szal-szulonel: hany valasz erkezett. Valasznal mindig 0. */
    threadReplyCount: z.number(),
    threadLastReplyAt: z.string().nullable(),
});
export const sendMessageInput = z.object({
    /**
     * A sima szoveges valtozat. Ha `body` is erkezik, a szerver SAJAT MAGA
     * szarmaztatja ujra a szoveget a dokumentumbol - a klienstol kapott erteket
     * nem hasznaljuk, kulonben a megjelenitett es a keresheto tartalom elterhetne.
     */
    text: z.string().max(MAX_MESSAGE_LENGTH),
    /**
     * A kliens generalja kuldes elott. Ez teszi a kuldest idempotenssé:
     * ujrakuldeskor a szerver az elso uzenetet adja vissza, nem hoz letre masodikat.
     */
    clientMsgId: z.string().min(1).max(64),
    parentId: z.string().nullable().optional(),
    body: richTextDocSchema.optional(),
    /** Feltoltott, mar kesz fajlok azonositoi. */
    fileIds: z.array(z.string()).max(10).optional(),
    /** Fontos-e - a kuldo jeloli be a szerkeszto fejlecében. */
    isImportant: z.boolean().optional(),
    /** Feladat-e - a "Fontos" melletti jelolo. */
    isTask: z.boolean().optional(),
});
/** Szoveg NELKUL is kuldheto uzenet, ha van hozza csatolt fajl. */
export const sendMessageInputChecked = sendMessageInput.refine((value) => value.text.trim().length > 0 || (value.fileIds?.length ?? 0) > 0, { message: 'Az üzenet nem lehet üres.' });
export const historyQuery = z.object({
    /** Lapozas visszafele: ennel a sorszamnal regebbieket ker. */
    beforeSeq: z.coerce.number().int().positive().optional(),
    /** Hezag-potlas: ennel a sorszamnal ujabbakat ker (novekvo sorrendben). */
    afterSeq: z.coerce.number().int().nonnegative().optional(),
    /**
     * Ugras egy uzenetre (permalink): a megadott sorszam KORNYEZETET adja vissza,
     * fele-fele aranyban elotte es utana. Igy egy kattintassal a helyere kerul a
     * beszelgetes, nem kell tobb korben visszafele lapozni.
     */
    aroundSeq: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const editMessageInput = z.object({
    text: z.string().min(1).max(MAX_MESSAGE_LENGTH),
    body: richTextDocSchema.optional(),
});
export const reactionInput = z.object({
    /** Rovid emoji-jel; a hosszkorlat a beragasztott szemet ellen ved. */
    emoji: z.string().min(1).max(64),
});
export const historyResponse = z.object({
    messages: z.array(messageSchema),
    /** Van-e REGEBBI uzenet a visszaadott ablak ELOTT. */
    hasMore: z.boolean(),
    /**
     * Van-e UJABB uzenet a visszaadott ablak UTAN.
     *
     * Permalinkkel egy regi uzenetre ugorva az ablak a csatorna KOZEPEN all: a
     * legalso sor nem a legfrissebb uzenet. Enelkul a kliens azt hinne, hogy a
     * lista vegen van - lefele nem toltene tovabb, es a beszelgetes ott ragadna
     * egy fel eves allapotban.
     */
    hasMoreNewer: z.boolean(),
    channelLastSeq: z.number(),
});
/**
 * Egy uzenet KORABBI valtozata - a szerkesztes elotti allapot.
 *
 * A lista elso eleme az EREDETI uzenet. A `replacedAt` azt mondja meg, mikor
 * valtotta fel egy ujabb valtozat - vagyis mikor szerkesztettek at.
 */
export const messageRevisionSchema = z.object({
    id: z.string(),
    text: z.string(),
    body: richTextDocSchema.nullable(),
    replacedAt: z.string(),
});
// ─── Mentett elemek (versenyaudit U7) ─────────────────────────────────────────
/**
 * A mentett elem allapota - a "kesobbre" lista teendokent is hasznalhato.
 * A kesz es az archivalt is mentett marad, csak mas fulon all.
 */
export const savedStatusSchema = z.enum(['active', 'done', 'archived']);
export const savedItemSchema = z.object({
    message: messageSchema,
    status: savedStatusSchema,
    savedAt: z.string(),
    /** Mikor kerult a mostani allapotba (kesz/archivalt). */
    statusAt: z.string().nullable(),
    /** A ra kert, meg el nem kuldott emlekezteto idopontja. */
    remindAt: z.string().nullable(),
});
export const updateSavedInput = z.object({
    status: savedStatusSchema.optional(),
    /** `null`: az emlekezteto torlese. */
    remindAt: z.string().nullable().optional(),
});
//# sourceMappingURL=message.js.map